/**
 * testDataService.ts
 *
 * Generates and clears demo test data for the GoDiving app.
 * All test records are tagged with { testData: true } so they can be
 * filtered out of the real app and bulk-deleted.
 *
 * Test data uses dive sites already loaded in the app — it never creates
 * new dive sites.
 */

import {
  getFirestore,
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  query,
  where,
  writeBatch,
  GeoPoint,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore'
import { geohashForLocation } from 'geofire-common'
import { getAllDiveSites } from './diveSiteService'
import Database from '../types/Database'

// ---------------------------------------------------------------------------
// Static test-user definitions (10 divers, Newcastle / Central Coast NSW)
// ---------------------------------------------------------------------------

interface TestUserDef {
  displayName: string
  email: string
  handle: string
  suburb: string
  lat: number
  lng: number
  padiLevel: string
  padiCertNumber: string
  padiEmail: string
  isGuide: boolean
}

const TEST_USERS: TestUserDef[] = [
  {
    displayName: 'Alex Turner',
    email: 'alex.turner.test@godiving.demo',
    handle: 'alex_turner_test',
    suburb: 'Newcastle NSW',
    lat: -32.9283,
    lng: 151.7817,
    padiLevel: 'Advanced Open Water Diver',
    padiCertNumber: 'PADI-TEST-001',
    padiEmail: 'alex.turner.test@padi.demo',
    isGuide: true
  },
  {
    displayName: 'Sarah Chen',
    email: 'sarah.chen.test@godiving.demo',
    handle: 'sarah_chen_test',
    suburb: 'Merewether NSW',
    lat: -32.9561,
    lng: 151.7662,
    padiLevel: 'Divemaster',
    padiCertNumber: 'PADI-TEST-002',
    padiEmail: 'sarah.chen.test@padi.demo',
    isGuide: true
  },
  {
    displayName: 'James Nguyen',
    email: 'james.nguyen.test@godiving.demo',
    handle: 'james_nguyen_test',
    suburb: 'The Entrance NSW',
    lat: -33.3469,
    lng: 151.4973,
    padiLevel: 'Open Water Diver',
    padiCertNumber: 'PADI-TEST-003',
    padiEmail: 'james.nguyen.test@padi.demo',
    isGuide: false
  },
  {
    displayName: 'Emma Walsh',
    email: 'emma.walsh.test@godiving.demo',
    handle: 'emma_walsh_test',
    suburb: 'Terrigal NSW',
    lat: -33.4453,
    lng: 151.4441,
    padiLevel: 'Advanced Open Water Diver',
    padiCertNumber: 'PADI-TEST-004',
    padiEmail: 'emma.walsh.test@padi.demo',
    isGuide: false
  },
  {
    displayName: 'Liam Patel',
    email: 'liam.patel.test@godiving.demo',
    handle: 'liam_patel_test',
    suburb: 'Gosford NSW',
    lat: -33.4241,
    lng: 151.3423,
    padiLevel: 'Rescue Diver',
    padiCertNumber: 'PADI-TEST-005',
    padiEmail: 'liam.patel.test@padi.demo',
    isGuide: true
  },
  {
    displayName: 'Olivia Brown',
    email: 'olivia.brown.test@godiving.demo',
    handle: 'olivia_brown_test',
    suburb: 'Swansea NSW',
    lat: -33.0861,
    lng: 151.6297,
    padiLevel: 'Open Water Diver',
    padiCertNumber: 'PADI-TEST-006',
    padiEmail: 'olivia.brown.test@padi.demo',
    isGuide: false
  },
  {
    displayName: 'Noah Williams',
    email: 'noah.williams.test@godiving.demo',
    handle: 'noah_williams_test',
    suburb: 'Redhead NSW',
    lat: -33.0239,
    lng: 151.6873,
    padiLevel: 'Divemaster',
    padiCertNumber: 'PADI-TEST-007',
    padiEmail: 'noah.williams.test@padi.demo',
    isGuide: true
  },
  {
    displayName: 'Chloe Martin',
    email: 'chloe.martin.test@godiving.demo',
    handle: 'chloe_martin_test',
    suburb: 'Wamberal NSW',
    lat: -33.4138,
    lng: 151.4562,
    padiLevel: 'Advanced Open Water Diver',
    padiCertNumber: 'PADI-TEST-008',
    padiEmail: 'chloe.martin.test@padi.demo',
    isGuide: false
  },
  {
    displayName: 'Ethan Scott',
    email: 'ethan.scott.test@godiving.demo',
    handle: 'ethan_scott_test',
    suburb: 'Norah Head NSW',
    lat: -33.2804,
    lng: 151.5741,
    padiLevel: 'Open Water Diver',
    padiCertNumber: 'PADI-TEST-009',
    padiEmail: 'ethan.scott.test@padi.demo',
    isGuide: false
  },
  {
    displayName: 'Mia Johnson',
    email: 'mia.johnson.test@godiving.demo',
    handle: 'mia_johnson_test',
    suburb: 'Lake Macquarie NSW',
    lat: -33.0966,
    lng: 151.5875,
    padiLevel: 'Rescue Diver',
    padiCertNumber: 'PADI-TEST-010',
    padiEmail: 'mia.johnson.test@padi.demo',
    isGuide: true
  }
]

// ---------------------------------------------------------------------------
// Dive descriptions seeded per dive number
// ---------------------------------------------------------------------------

const DIVE_DESCRIPTIONS = [
  'Beautiful wall dive with great visibility. Expect to see blue gropers, wobbegong sharks and plenty of nudibranchs along the rocky reef.',
  'Relaxed morning dive on a sandy bottom with healthy sea grass beds. Ideal for spotting stingrays, flathead and the occasional seahorse.',
  'Drift dive along a kelp forest. Current runs north to south — keep an eye out for yellow-finned leatherjackets and old wives.',
  'Night dive on a protected cove. The bioluminescence here is stunning and you will almost certainly encounter lionfish and octopus.',
  'Wreck exploration in 18m of water. The hull is encrusted with corals and home to a large school of yellowtail scad.',
  'Shallow reef perfect for beginners. Friendly resident blue groupers and a cleaning station that regularly attracts large rays.',
  'Boulder reef with swim-throughs. Look out for the large lobster colony under the overhang at 12m and the resident wobbegong at the southern end.',
  'Exposed point dive best at slack tide. Schooling silver biddies and bream create an impressive moving curtain of fish.',
  'Sandy lagoon dive with vibrant sea grass. Slow down and look for ornate ghost pipefish, pygmy seahorses and various crustaceans hiding in the grass.',
  'Granite pinnacle rising from 30m to 8m. Expect strong surge near the top but fantastic sponge gardens on the shaded faces below 20m.'
]

const MEETING_POINTS = [
  'Main beach carpark — meet at the northern end by the lifeguard tower',
  'Boat ramp carpark — look for the blue GoDiving banner',
  'Foreshore reserve picnic tables near the entry steps',
  'Beachside café adjacent to the surf club — I will have a coffee on the go',
  'Council carpark at the end of the headland road'
]

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function daysFromNow(days: number): Date {
  const d = new Date()
  d.setDate(d.getDate() + days)
  d.setHours(8 + (days % 4), 0, 0, 0) // 08:00, 09:00, 10:00, 11:00 rotating
  return d
}

function pastDate(daysAgo: number): Date {
  const d = new Date()
  d.setDate(d.getDate() - daysAgo)
  d.setHours(9, 0, 0, 0)
  return d
}

function pick<T>(arr: T[], index: number): T {
  return arr[index % arr.length]
}

// ---------------------------------------------------------------------------
// Main export: generateTestData
// ---------------------------------------------------------------------------

export async function generateTestData(): Promise<void> {
  // 1. Wipe any existing test data first for idempotency
  await clearTestData()

  const db = getFirestore()
  const now = new Date()

  // 2. Load real dive sites — never create new ones
  const diveSites = await getAllDiveSites()
  if (diveSites.length === 0) {
    throw new Error(
      'No dive sites found in the database. Please add dive sites before generating test data.'
    )
  }

  // ---------------------------------------------------------------------------
  // 3. Create test users
  // ---------------------------------------------------------------------------
  const padiDocUrl =
    'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a7/Camponotus_flavomarginatus_ant.jpg/320px-Camponotus_flavomarginatus_ant.jpg'

  const userIds: string[] = []

  for (const u of TEST_USERS) {
    const submittedAt = Timestamp.fromDate(pastDate(60))
    const reviewedAt = Timestamp.fromDate(pastDate(59))

    const accreditation: Database.PadiAccreditation = {
      type: 'padi',
      level: u.padiLevel,
      fullName: u.displayName,
      dateOfBirth: Timestamp.fromDate(new Date('1990-01-01')),
      certificationNumber: u.padiCertNumber,
      optionalMemberId: null,
      registeredEmail: u.padiEmail,
      documentUrl: padiDocUrl,
      guidedDiveDocuments: u.isGuide
        ? {
            professionalStatusUrl: padiDocUrl,
            professionalStatusExpiry: Timestamp.fromDate(
              new Date(now.getFullYear() + 2, 0, 1)
            ),
            insuranceUrl: padiDocUrl,
            insuranceExpiry: Timestamp.fromDate(
              new Date(now.getFullYear() + 1, 11, 31)
            ),
            affiliatedCenter: null,
            localRegulationsAcknowledged: true
          }
        : null,
      status: 'verified',
      submittedAt,
      reviewedAt,
      reviewedBy: 'test-data-admin'
    }

    const userRef = await addDoc(collection(db, 'users'), {
      displayName: u.displayName,
      email: u.email,
      handle: u.handle,
      suburb: u.suburb,
      location: new GeoPoint(u.lat, u.lng),
      role: 'member',
      guidedDiveFlag: u.isGuide,
      profileComplete: true,
      creditBalance: 0,
      accreditation,
      bankDetails: u.isGuide
        ? {
            accountName: u.displayName,
            bsb: '012-345',
            accountNumber: `${100000000 + TEST_USERS.indexOf(u)}`
          }
        : null,
      allowlist: null,
      perfectRatingCount: 0,
      ratingAggregates: null,
      platformFeeOverride: null,
      accountStatus: 'active',
      testData: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    })

    // Register the handle lookup document
    await addDoc(collection(db, 'handles'), {
      // Store handle -> uid mapping; doc id would normally be the handle itself
      // but addDoc generates an id — this is a best-effort for test data only
      handle: u.handle,
      uid: userRef.id,
      testData: true
    })

    userIds.push(userRef.id)
  }

  // Guide user IDs (those with isGuide: true)
  const guideIndices = TEST_USERS.map((u, i) => (u.isGuide ? i : -1)).filter(
    i => i >= 0
  )
  const memberIndices = TEST_USERS.map((u, i) => (!u.isGuide ? i : -1)).filter(
    i => i >= 0
  )

  // ---------------------------------------------------------------------------
  // 4. Create 30 dives spread across Newcastle / Central Coast dive sites,
  //    using only real dive site names/locations from the database.
  // ---------------------------------------------------------------------------

  // Build a mix: future dives (24), past completed (6)
  type DiveSpec = {
    siteIndex: number
    hostUserIndex: number
    type: 'buddy' | 'guided'
    daysOffset: number // positive = future, negative = past
    status: Database.Dive['status']
    depthTier: '18m' | '30m' | '30m+'
    duration: number
    price: number | null
    maxParticipants: number
    descIndex: number
    meetingIndex: number
    notes: string | null
  }

  const diveSpecs: DiveSpec[] = [
    // --- Future open dives (guided) ---
    {
      siteIndex: 0,
      hostUserIndex: 0,
      type: 'guided',
      daysOffset: 3,
      status: 'open',
      depthTier: '18m',
      duration: 60,
      price: 80,
      maxParticipants: 6,
      descIndex: 0,
      meetingIndex: 0,
      notes: 'BYO wetsuit. Equipment hire available.'
    },
    {
      siteIndex: 1,
      hostUserIndex: 1,
      type: 'guided',
      daysOffset: 5,
      status: 'open',
      depthTier: '30m',
      duration: 75,
      price: 95,
      maxParticipants: 4,
      descIndex: 1,
      meetingIndex: 1,
      notes: 'Advanced certification required.'
    },
    {
      siteIndex: 2,
      hostUserIndex: 4,
      type: 'guided',
      daysOffset: 7,
      status: 'open',
      depthTier: '18m',
      duration: 60,
      price: 75,
      maxParticipants: 8,
      descIndex: 2,
      meetingIndex: 2,
      notes: null
    },
    {
      siteIndex: 3,
      hostUserIndex: 6,
      type: 'guided',
      daysOffset: 8,
      status: 'open',
      depthTier: '30m+',
      duration: 90,
      price: 120,
      maxParticipants: 4,
      descIndex: 3,
      meetingIndex: 3,
      notes: 'Night dive. Torch provided.'
    },
    {
      siteIndex: 4,
      hostUserIndex: 9,
      type: 'guided',
      daysOffset: 10,
      status: 'open',
      depthTier: '30m',
      duration: 75,
      price: 100,
      maxParticipants: 6,
      descIndex: 4,
      meetingIndex: 4,
      notes: null
    },
    {
      siteIndex: 0,
      hostUserIndex: 0,
      type: 'guided',
      daysOffset: 12,
      status: 'open',
      depthTier: '18m',
      duration: 60,
      price: 80,
      maxParticipants: 6,
      descIndex: 5,
      meetingIndex: 0,
      notes: 'Suitable for Open Water certification holders.'
    },
    {
      siteIndex: 1,
      hostUserIndex: 1,
      type: 'guided',
      daysOffset: 14,
      status: 'open',
      depthTier: '30m',
      duration: 80,
      price: 90,
      maxParticipants: 5,
      descIndex: 6,
      meetingIndex: 1,
      notes: null
    },
    {
      siteIndex: 2,
      hostUserIndex: 4,
      type: 'guided',
      daysOffset: 16,
      status: 'open',
      depthTier: '18m',
      duration: 60,
      price: 70,
      maxParticipants: 8,
      descIndex: 7,
      meetingIndex: 2,
      notes: 'Shore entry dive.'
    },
    {
      siteIndex: 3,
      hostUserIndex: 6,
      type: 'guided',
      daysOffset: 18,
      status: 'open',
      depthTier: '30m',
      duration: 75,
      price: 95,
      maxParticipants: 4,
      descIndex: 8,
      meetingIndex: 3,
      notes: null
    },
    {
      siteIndex: 4,
      hostUserIndex: 9,
      type: 'guided',
      daysOffset: 20,
      status: 'open',
      depthTier: '30m+',
      duration: 90,
      price: 130,
      maxParticipants: 4,
      descIndex: 9,
      meetingIndex: 4,
      notes: 'Nitrox recommended.'
    },
    {
      siteIndex: 0,
      hostUserIndex: 0,
      type: 'guided',
      daysOffset: 22,
      status: 'open',
      depthTier: '18m',
      duration: 60,
      price: 80,
      maxParticipants: 6,
      descIndex: 0,
      meetingIndex: 0,
      notes: null
    },
    {
      siteIndex: 1,
      hostUserIndex: 1,
      type: 'guided',
      daysOffset: 25,
      status: 'open',
      depthTier: '30m',
      duration: 75,
      price: 90,
      maxParticipants: 5,
      descIndex: 1,
      meetingIndex: 1,
      notes: null
    },
    // --- Future open dives (buddy) ---
    {
      siteIndex: 2,
      hostUserIndex: 2,
      type: 'buddy',
      daysOffset: 4,
      status: 'open',
      depthTier: '18m',
      duration: 45,
      price: null,
      maxParticipants: 3,
      descIndex: 2,
      meetingIndex: 2,
      notes: 'Looking for a calm dive after work.'
    },
    {
      siteIndex: 3,
      hostUserIndex: 3,
      type: 'buddy',
      daysOffset: 6,
      status: 'open',
      depthTier: '18m',
      duration: 60,
      price: null,
      maxParticipants: 3,
      descIndex: 3,
      meetingIndex: 3,
      notes: null
    },
    {
      siteIndex: 4,
      hostUserIndex: 5,
      type: 'buddy',
      daysOffset: 9,
      status: 'open',
      depthTier: '30m',
      duration: 60,
      price: null,
      maxParticipants: 3,
      descIndex: 4,
      meetingIndex: 4,
      notes: 'Advanced or Rescue cert preferred.'
    },
    {
      siteIndex: 0,
      hostUserIndex: 7,
      type: 'buddy',
      daysOffset: 11,
      status: 'open',
      depthTier: '18m',
      duration: 45,
      price: null,
      maxParticipants: 3,
      descIndex: 5,
      meetingIndex: 0,
      notes: null
    },
    {
      siteIndex: 1,
      hostUserIndex: 8,
      type: 'buddy',
      daysOffset: 13,
      status: 'open',
      depthTier: '18m',
      duration: 60,
      price: null,
      maxParticipants: 3,
      descIndex: 6,
      meetingIndex: 1,
      notes: 'Bringing my new camera rig.'
    },
    {
      siteIndex: 2,
      hostUserIndex: 2,
      type: 'buddy',
      daysOffset: 15,
      status: 'open',
      depthTier: '30m',
      duration: 60,
      price: null,
      maxParticipants: 3,
      descIndex: 7,
      meetingIndex: 2,
      notes: null
    },
    {
      siteIndex: 3,
      hostUserIndex: 3,
      type: 'buddy',
      daysOffset: 17,
      status: 'open',
      depthTier: '18m',
      duration: 45,
      price: null,
      maxParticipants: 3,
      descIndex: 8,
      meetingIndex: 3,
      notes: null
    },
    {
      siteIndex: 4,
      hostUserIndex: 5,
      type: 'buddy',
      daysOffset: 19,
      status: 'open',
      depthTier: '18m',
      duration: 60,
      price: null,
      maxParticipants: 3,
      descIndex: 9,
      meetingIndex: 4,
      notes: 'Early start — sunrise dive!'
    },
    {
      siteIndex: 0,
      hostUserIndex: 7,
      type: 'buddy',
      daysOffset: 21,
      status: 'open',
      depthTier: '30m',
      duration: 60,
      price: null,
      maxParticipants: 3,
      descIndex: 0,
      meetingIndex: 0,
      notes: null
    },
    {
      siteIndex: 1,
      hostUserIndex: 8,
      type: 'buddy',
      daysOffset: 24,
      status: 'open',
      depthTier: '18m',
      duration: 45,
      price: null,
      maxParticipants: 3,
      descIndex: 1,
      meetingIndex: 1,
      notes: null
    },
    // --- Future full dives ---
    {
      siteIndex: 2,
      hostUserIndex: 9,
      type: 'guided',
      daysOffset: 2,
      status: 'full',
      depthTier: '18m',
      duration: 60,
      price: 80,
      maxParticipants: 4,
      descIndex: 2,
      meetingIndex: 2,
      notes: null
    },
    {
      siteIndex: 3,
      hostUserIndex: 0,
      type: 'buddy',
      daysOffset: 1,
      status: 'full',
      depthTier: '18m',
      duration: 45,
      price: null,
      maxParticipants: 3,
      descIndex: 3,
      meetingIndex: 3,
      notes: null
    },
    // --- Past completed dives ---
    {
      siteIndex: 0,
      hostUserIndex: 1,
      type: 'guided',
      daysOffset: -10,
      status: 'completed',
      depthTier: '30m',
      duration: 75,
      price: 90,
      maxParticipants: 5,
      descIndex: 4,
      meetingIndex: 4,
      notes: null
    },
    {
      siteIndex: 1,
      hostUserIndex: 4,
      type: 'guided',
      daysOffset: -14,
      status: 'completed',
      depthTier: '18m',
      duration: 60,
      price: 75,
      maxParticipants: 6,
      descIndex: 5,
      meetingIndex: 0,
      notes: null
    },
    {
      siteIndex: 2,
      hostUserIndex: 6,
      type: 'buddy',
      daysOffset: -7,
      status: 'completed',
      depthTier: '18m',
      duration: 45,
      price: null,
      maxParticipants: 3,
      descIndex: 6,
      meetingIndex: 1,
      notes: null
    },
    {
      siteIndex: 3,
      hostUserIndex: 9,
      type: 'buddy',
      daysOffset: -3,
      status: 'completed',
      depthTier: '30m',
      duration: 60,
      price: null,
      maxParticipants: 3,
      descIndex: 7,
      meetingIndex: 2,
      notes: null
    },
    // --- Past cancelled dive ---
    {
      siteIndex: 4,
      hostUserIndex: 0,
      type: 'guided',
      daysOffset: -5,
      status: 'cancelled',
      depthTier: '18m',
      duration: 60,
      price: 80,
      maxParticipants: 6,
      descIndex: 8,
      meetingIndex: 3,
      notes: null
    },
    // --- Removed dive ---
    {
      siteIndex: 1,
      hostUserIndex: 1,
      type: 'guided',
      daysOffset: -20,
      status: 'removed',
      depthTier: '30m',
      duration: 75,
      price: 95,
      maxParticipants: 4,
      descIndex: 9,
      meetingIndex: 4,
      notes: null
    }
  ]

  const diveIds: string[] = []

  for (let i = 0; i < diveSpecs.length; i++) {
    const spec = diveSpecs[i]
    const site = diveSites[spec.siteIndex % diveSites.length]
    const lat = site.location.latitude
    const lng = site.location.longitude
    const geohash = geohashForLocation([lat, lng])
    const diveDate =
      spec.daysOffset >= 0
        ? daysFromNow(spec.daysOffset)
        : pastDate(Math.abs(spec.daysOffset))

    const hostUserId =
      userIds[
        guideIndices.includes(spec.hostUserIndex)
          ? spec.hostUserIndex
          : guideIndices[spec.hostUserIndex % guideIndices.length]
      ]

    const diveRef = await addDoc(collection(db, 'dives'), {
      hostId: hostUserId,
      type: spec.type,
      siteName: site.name,
      description: pick(DIVE_DESCRIPTIONS, spec.descIndex),
      location: new GeoPoint(lat, lng),
      geohash,
      dateTime: Timestamp.fromDate(diveDate),
      duration: spec.duration,
      depthTier: spec.depthTier,
      price: spec.price,
      maxParticipants: spec.maxParticipants,
      meetingPoint: pick(MEETING_POINTS, spec.meetingIndex),
      notes: spec.notes,
      status: spec.status,
      pendingRequestCount: 0,
      moderationReason:
        spec.status === 'removed'
          ? 'Test data: listing removed for demo purposes'
          : null,
      moderatedBy: spec.status === 'removed' ? 'test-data-admin' : null,
      moderatedAt:
        spec.status === 'removed' ? Timestamp.fromDate(pastDate(15)) : null,
      previousStatus: spec.status === 'removed' ? 'open' : null,
      testData: true,
      createdAt: serverTimestamp()
    })

    diveIds.push(diveRef.id)
  }

  // ---------------------------------------------------------------------------
  // 5. Create join requests — one per member for each open/full/completed dive
  // ---------------------------------------------------------------------------

  // Pairs: dive index -> requesting member user index
  const joinPairs: Array<{
    diveIdx: number
    memberUserIdx: number
    status: Database.JoinRequestStatus
    paymentStatus: Database.JoinRequestPaymentStatus
  }> = [
    // Completed dives get approved + paid join requests
    {
      diveIdx: 24,
      memberUserIdx: memberIndices[0],
      status: 'approved',
      paymentStatus: 'paid'
    },
    {
      diveIdx: 24,
      memberUserIdx: memberIndices[1],
      status: 'approved',
      paymentStatus: 'paid'
    },
    {
      diveIdx: 25,
      memberUserIdx: memberIndices[2],
      status: 'approved',
      paymentStatus: 'paid'
    },
    {
      diveIdx: 26,
      memberUserIdx: memberIndices[0],
      status: 'approved',
      paymentStatus: null
    },
    {
      diveIdx: 27,
      memberUserIdx: memberIndices[1],
      status: 'approved',
      paymentStatus: null
    },
    // Open guided dives get pending requests
    {
      diveIdx: 0,
      memberUserIdx: memberIndices[0],
      status: 'pending',
      paymentStatus: null
    },
    {
      diveIdx: 0,
      memberUserIdx: memberIndices[1],
      status: 'pending',
      paymentStatus: null
    },
    {
      diveIdx: 1,
      memberUserIdx: memberIndices[2],
      status: 'pending',
      paymentStatus: null
    },
    {
      diveIdx: 2,
      memberUserIdx: memberIndices[0],
      status: 'pending',
      paymentStatus: null
    },
    {
      diveIdx: 3,
      memberUserIdx: memberIndices[1],
      status: 'pending',
      paymentStatus: null
    },
    // Full dives have approved requests
    {
      diveIdx: 22,
      memberUserIdx: memberIndices[0],
      status: 'approved',
      paymentStatus: 'awaiting'
    },
    {
      diveIdx: 22,
      memberUserIdx: memberIndices[1],
      status: 'approved',
      paymentStatus: 'awaiting'
    },
    {
      diveIdx: 23,
      memberUserIdx: memberIndices[2],
      status: 'approved',
      paymentStatus: null
    },
    // A declined request
    {
      diveIdx: 4,
      memberUserIdx: memberIndices[2],
      status: 'declined',
      paymentStatus: null
    }
  ]

  const joinRequestIds: string[] = []

  for (const jp of joinPairs) {
    const requesterUserId = userIds[jp.memberUserIdx]
    const diveId = diveIds[jp.diveIdx]
    if (!diveId || !requesterUserId) continue

    const jrRef = await addDoc(collection(db, 'joinRequests'), {
      diveId,
      requesterId: requesterUserId,
      status: jp.status,
      paymentStatus: jp.paymentStatus,
      paymentDeadline:
        jp.paymentStatus === 'awaiting'
          ? Timestamp.fromDate(daysFromNow(2))
          : null,
      testData: true,
      createdAt: serverTimestamp()
    })
    joinRequestIds.push(jrRef.id)
  }

  // ---------------------------------------------------------------------------
  // 6. Create transactions for paid completed guided dives
  // ---------------------------------------------------------------------------

  // Pairs: diveIdx -> payerMemberIdx, host guideIdx, amount
  const txPairs: Array<{
    diveIdx: number
    payerUserIdx: number
    hostUserIdx: number
    amount: number
  }> = [
    { diveIdx: 24, payerUserIdx: memberIndices[0], hostUserIdx: 1, amount: 90 },
    { diveIdx: 24, payerUserIdx: memberIndices[1], hostUserIdx: 1, amount: 90 },
    { diveIdx: 25, payerUserIdx: memberIndices[2], hostUserIdx: 4, amount: 75 }
  ]

  for (const tx of txPairs) {
    const stripeFee = parseFloat((tx.amount * 0.0175 + 0.3).toFixed(2))
    const platformCommission = parseFloat((tx.amount * 0.05).toFixed(2))
    const netOwedToHost = parseFloat(
      (tx.amount - stripeFee - platformCommission).toFixed(2)
    )

    await addDoc(collection(db, 'transactions'), {
      diveId: diveIds[tx.diveIdx],
      payerId: userIds[tx.payerUserIdx],
      hostId: userIds[tx.hostUserIdx],
      amount: tx.amount,
      stripeFee,
      platformCommission,
      netOwedToHost,
      stripePaymentIntentId: `pi_test_${Math.random()
        .toString(36)
        .slice(2, 18)}`,
      payoutStatus: 'pending',
      refundType: null,
      refundAmount: null,
      adminNotes: null,
      payoutProcessedAt: null,
      payoutProcessedBy: null,
      refund: null,
      testData: true,
      createdAt: serverTimestamp()
    })
  }

  // ---------------------------------------------------------------------------
  // 7. Create ratings for completed dives
  // ---------------------------------------------------------------------------

  // Participants rating each other on completed dives
  const ratingPairs: Array<{
    diveIdx: number
    raterUserIdx: number
    rateeUserIdx: number
    diveType: 'buddy' | 'guided'
    comment: string | null
  }> = [
    {
      diveIdx: 24,
      raterUserIdx: memberIndices[0],
      rateeUserIdx: 1,
      diveType: 'guided',
      comment: 'Fantastic guide — very knowledgeable about the local reef.'
    },
    {
      diveIdx: 24,
      raterUserIdx: memberIndices[1],
      rateeUserIdx: 1,
      diveType: 'guided',
      comment: 'Great dive, excellent safety briefing.'
    },
    {
      diveIdx: 25,
      raterUserIdx: memberIndices[2],
      rateeUserIdx: 4,
      diveType: 'guided',
      comment: null
    },
    {
      diveIdx: 26,
      raterUserIdx: memberIndices[0],
      rateeUserIdx: 2,
      diveType: 'buddy',
      comment: 'Really fun buddy dive, would definitely go again.'
    },
    {
      diveIdx: 27,
      raterUserIdx: memberIndices[1],
      rateeUserIdx: 3,
      diveType: 'buddy',
      comment: null
    }
  ]

  for (const rp of ratingPairs) {
    const raterUserId = userIds[rp.raterUserIdx]
    const rateeUserId = userIds[rp.rateeUserIdx]
    const diveId = diveIds[rp.diveIdx]
    if (!raterUserId || !rateeUserId || !diveId) continue

    await addDoc(collection(db, 'ratings'), {
      diveId,
      raterId: raterUserId,
      rateeId: rateeUserId,
      showedUp: true,
      divedResponsibly: true,
      knewGearSetup: true,
      goodBuddy: true,
      wouldDiveAgain: Math.random() > 0.2,
      comment: rp.comment,
      timestamp: Timestamp.fromDate(
        pastDate(Math.floor(Math.random() * 5) + 1)
      ),
      diveType: rp.diveType,
      testData: true
    })
  }
}

// ---------------------------------------------------------------------------
// clearTestData — deletes all documents tagged with testData: true
// across all collections that can contain test data.
// ---------------------------------------------------------------------------

const TEST_DATA_COLLECTIONS = [
  'dives',
  'users',
  'joinRequests',
  'transactions',
  'ratings',
  'handles'
] as const

export async function clearTestData(): Promise<number> {
  const db = getFirestore()
  let totalDeleted = 0

  for (const col of TEST_DATA_COLLECTIONS) {
    const q = query(collection(db, col), where('testData', '==', true))
    const snapshot = await getDocs(q)

    // Firestore batches are capped at 500 writes — chunk if needed
    const docs = snapshot.docs
    for (let i = 0; i < docs.length; i += 500) {
      const batch = writeBatch(db)
      docs.slice(i, i + 500).forEach(d => batch.delete(doc(db, col, d.id)))
      await batch.commit()
      totalDeleted += docs.slice(i, i + 500).length
    }
  }

  return totalDeleted
}
